import type { DateTime } from "luxon";
import {
	agencyNameOf,
	approvedCountOf,
	autoPostRationale,
	between,
	COIN_FLIP_PERCENT,
	creatorOf,
	type DemoOriginsContext,
	leadMinutesOf,
	percent,
	pickRosterNurse,
	posterOf,
	rosterNurseById,
	seededIndex,
	timeOffDays,
	toPerson,
	weightedKind,
} from "./demoRequestOriginSeed";
import { CallOffChannel, NotificationType, type Opening, RequestOriginKind, type RequestOrigins, RequestSource, type ShiftRequest } from "./models";
import { ORIGIN_GROUP_BY_KIND } from "./requestOrigins";

/**
 * Demo-only request origins. Real fields decide the cause wherever they name one (a call-off, a replace request, a
 * Clipboard shift); a plain post is given a seeded one. The real resolver replaces this file, `demoRequestOriginSeed.ts`
 * and `useDemoOriginsContext.ts`; nothing outside `useRequestOrigins` imports them.
 */
type SharedFields = Pick<Opening, "at" | "slotsAdded" | "leadMinutes">;
type BuildOptions = { salt: string; excludeNurseId: number | null; allowRatio: boolean; total: number };

/** How many posted requests get a second cause, and within how long after the first. */
const SECOND_CAUSE_PERCENT = 30;
const SECOND_CAUSE_WINDOW_MINUTES = 6 * 60;
const SECOND_CAUSE_EARLIEST_FRACTION = 0.1;
const CLIPBOARD_AGENCY_NAME = "Clipboard";
const CHANNELS: readonly CallOffChannel[] = [CallOffChannel.App, CallOffChannel.Phone, CallOffChannel.Text, CallOffChannel.Admin, CallOffChannel.Annie];

/** The cause the request's own fields name, or `null` for a plain post, which is seeded instead. */
const realKindOf = (request: ShiftRequest, ctx: DemoOriginsContext): RequestOriginKind | null => {
	if (request.request_type === NotificationType.ReplaceShift) return RequestOriginKind.ReplaceRequest;
	if (request.request_type === NotificationType.Swap) return RequestOriginKind.SwapRequest;
	if (request.request_type === NotificationType.ReplaceAgency) return RequestOriginKind.AgencyCanceled;
	if (request.source === null || request.source === undefined) return agencyNameOf(request) === null ? null : RequestOriginKind.Agency;
	switch (request.source) {
		case RequestSource.CallOff:
			return RequestOriginKind.CallOff;
		case RequestSource.Pto:
			return ctx.hrisSource !== null && percent(request, "hris") < COIN_FLIP_PERCENT ? RequestOriginKind.TimeOffHris : RequestOriginKind.TimeOff;
		case RequestSource.Clipboard:
			return RequestOriginKind.Agency;
		case RequestSource.CancelAgency:
			return RequestOriginKind.AgencyCanceled;
		case RequestSource.PostShiftRecommendation:
			return RequestOriginKind.FromRecommendation;
		// The demo seeder writes every open shift it creates as an auto-post, so read literally a demo list would be one
		// kind top to bottom; plain and seeder posts get the seeded spread instead.
		case RequestSource.PostShift:
		case RequestSource.AutoPostShift:
			return agencyNameOf(request) === null ? null : RequestOriginKind.Agency;
		default: {
			const unhandled: never = request.source;
			throw new Error(`Unhandled request source ${String(unhandled)}`);
		}
	}
};

const seededKindOf = (request: ShiftRequest, ctx: DemoOriginsContext): RequestOriginKind => {
	const kind = weightedKind(request, "kind");
	return kind === RequestOriginKind.TimeOffHris && ctx.hrisSource === null ? RequestOriginKind.TimeOff : kind;
};

/** A cause that names a nurse, or `null` when the roster has nobody to name. */
const nurseOpening = (kind: RequestOriginKind, request: ShiftRequest, shared: SharedFields, ctx: DemoOriginsContext, options: BuildOptions): Opening | null => {
	const rosterNurse = pickRosterNurse(request, `${options.salt}:nurse`, ctx, options.excludeNurseId);
	if (rosterNurse === null) return null;
	const nurse = toPerson(rosterNurse, ctx);
	const common = { ...shared, group: ORIGIN_GROUP_BY_KIND[kind] };
	switch (kind) {
		case RequestOriginKind.CallOff: {
			const channel = CHANNELS[seededIndex(CHANNELS.length, `${request.id}:${options.salt}:channel`)];
			return { ...common, kind, nurse, channel, actor: channel === CallOffChannel.Admin ? posterOf(request, ctx) : nurse };
		}
		case RequestOriginKind.TimeOff:
			return { ...common, kind, nurse, ...timeOffDays(request, ctx), actor: creatorOf(request, ctx) };
		case RequestOriginKind.TimeOffHris:
			return ctx.hrisSource === null ? null : { ...common, kind, nurse, ...timeOffDays(request, ctx), hrisSource: ctx.hrisSource, actor: null };
		case RequestOriginKind.Deactivation:
			return { ...common, kind, nurse, actor: creatorOf(request, ctx) };
		default:
			return null;
	}
};

const agencyCanceledOpening = (request: ShiftRequest, shared: SharedFields, ctx: DemoOriginsContext, salt: string): Opening | null => {
	const replaced = "replacement_nurse_id" in request ? rosterNurseById(ctx, request.replacement_nurse_id) : null;
	const nurse = replaced !== null ? replaced : pickRosterNurse(request, `${salt}:agency-nurse`, ctx, null);
	if (nurse === null) return null;
	// DEFAULT: demo only — a roster nurse with no agency on file reads as a Clipboard worker.
	const agencyName = agencyNameOf(request) ?? nurse.agencyName ?? CLIPBOARD_AGENCY_NAME;
	return {
		...shared,
		group: ORIGIN_GROUP_BY_KIND[RequestOriginKind.AgencyCanceled],
		actor: null,
		kind: RequestOriginKind.AgencyCanceled,
		nurse: toPerson(nurse, ctx),
		agencyName,
	};
};

/** The staff member behind a replace or swap request: its own nurse, or a seeded one of the right role. */
const requesterOf = (kind: RequestOriginKind.ReplaceRequest | RequestOriginKind.SwapRequest, request: ShiftRequest, ctx: DemoOriginsContext) => {
	const own =
		kind === RequestOriginKind.ReplaceRequest && "replacement_nurse_id" in request
			? rosterNurseById(ctx, request.replacement_nurse_id)
			: (ctx.roster.find((nurse) => nurse.userId === request.created_by) ?? null);
	const nurse = own !== null ? own : pickRosterNurse(request, "requester", ctx, null);
	return nurse === null ? null : toPerson(nurse, ctx);
};

const buildOpening = (kind: RequestOriginKind, request: ShiftRequest, shared: SharedFields, ctx: DemoOriginsContext, options: BuildOptions): Opening | null => {
	const common = { ...shared, group: ORIGIN_GROUP_BY_KIND[kind] };
	switch (kind) {
		case RequestOriginKind.CallOff:
		case RequestOriginKind.TimeOff:
		case RequestOriginKind.TimeOffHris:
		case RequestOriginKind.Deactivation:
			return nurseOpening(kind, request, shared, ctx, options);
		case RequestOriginKind.AgencyCanceled:
			return agencyCanceledOpening(request, shared, ctx, options.salt);
		case RequestOriginKind.AutoPost:
			return { ...common, kind, actor: null, rationale: autoPostRationale(request, ctx, options.allowRatio) };
		case RequestOriginKind.Agency:
			// DEFAULT: demo only — a Clipboard-sourced request without a stored agency name is Clipboard's.
			return { ...common, kind, actor: null, agencyName: agencyNameOf(request) ?? CLIPBOARD_AGENCY_NAME };
		case RequestOriginKind.CountRaised:
			return { ...common, kind, actor: posterOf(request, ctx), from: options.total - 1, to: options.total };
		case RequestOriginKind.ReplaceRequest:
		case RequestOriginKind.SwapRequest:
			return { ...common, kind, actor: requesterOf(kind, request, ctx) };
		case RequestOriginKind.PostedByPerson:
		case RequestOriginKind.FromRecommendation:
		case RequestOriginKind.PostedByAnnie:
			return { ...common, kind, actor: posterOf(request, ctx) };
		default: {
			const unhandled: never = kind;
			throw new Error(`Unhandled origin kind ${String(unhandled)}`);
		}
	}
};

/** A cause that names a nurse, with no eligible roster nurse, is told as a plain post rather than dropped. */
const buildOrPost = (kind: RequestOriginKind, request: ShiftRequest, shared: SharedFields, ctx: DemoOriginsContext, options: BuildOptions): Opening => {
	const opening = buildOpening(kind, request, shared, ctx, options);
	if (opening !== null) return opening;
	return { ...shared, group: ORIGIN_GROUP_BY_KIND[RequestOriginKind.PostedByPerson], kind: RequestOriginKind.PostedByPerson, actor: posterOf(request, ctx) };
};

/** Which cause added the request's later slot, and when — often automation answering a call-off, or the reverse. */
const secondCauseOf = (
	request: ShiftRequest,
	firstKind: RequestOriginKind,
	total: number,
	ctx: DemoOriginsContext,
): { kind: RequestOriginKind; at: DateTime } | null => {
	if (total < 2 || percent(request, "second") >= SECOND_CAUSE_PERCENT) return null;
	const fraction = between(request, "second-at", SECOND_CAUSE_EARLIEST_FRACTION, 1);
	const at = request.created_at.plus({ minutes: Math.round(fraction * SECOND_CAUSE_WINDOW_MINUTES) });
	if (at > ctx.now || at >= request.message.shift_start) return null;
	switch (firstKind) {
		case RequestOriginKind.CallOff:
			return { kind: RequestOriginKind.AutoPost, at };
		case RequestOriginKind.AutoPost:
			return { kind: RequestOriginKind.CallOff, at };
		case RequestOriginKind.PostedByPerson:
			return { kind: percent(request, "second-kind") < COIN_FLIP_PERCENT ? RequestOriginKind.AutoPost : RequestOriginKind.CountRaised, at };
		default:
			return null;
	}
};

/**
 * Slots add up to the request: all it ever opened is what is still open (`needed_nurses`) plus what was filled. The
 * later cause adds one; the first opened the rest.
 */
export const demoOriginsOf = (request: ShiftRequest, ctx: DemoOriginsContext): RequestOrigins => {
	const total = request.request_type === NotificationType.OpenShift && "needed_nurses" in request ? request.needed_nurses + approvedCountOf(request) : 1;
	const realKind = realKindOf(request, ctx);
	const firstKind = realKind === null ? seededKindOf(request, ctx) : realKind;
	const second = secondCauseOf(request, firstKind, total, ctx);
	const firstSlots = Math.max(1, total - (second === null ? 0 : 1));
	const firstShared = { at: request.created_at, slotsAdded: firstSlots, leadMinutes: leadMinutesOf(request, request.created_at) };
	const first = buildOrPost(firstKind, request, firstShared, ctx, { salt: "first", excludeNurseId: null, allowRatio: firstSlots === 1, total });
	if (second === null) return { openings: [first] };
	const secondShared = { at: second.at, slotsAdded: 1, leadMinutes: leadMinutesOf(request, second.at) };
	const excludeNurseId = first.kind === RequestOriginKind.CallOff ? first.nurse.nurseId : null;
	return { openings: [first, buildOrPost(second.kind, request, secondShared, ctx, { salt: "second", excludeNurseId, allowRatio: true, total })] };
};

/** What a request's origins are seeded from, so an unchanged request keeps the same objects across refreshes. */
export const demoOriginsSignature = (request: ShiftRequest): string =>
	JSON.stringify([
		request.created_at.toMillis(),
		request.created_at.zoneName,
		request.request_type,
		request.source,
		request.created_by,
		request.job_id,
		request.location,
		request.message.roles,
		request.message.shift_start.toMillis(),
		request.message.shift_start.zoneName,
		"agency_source_name" in request ? request.agency_source_name : null,
		"replacement_nurse_id" in request ? request.replacement_nurse_id : null,
		"needed_nurses" in request ? request.needed_nurses : null,
		approvedCountOf(request),
	]);
